import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Save, Upload, X, Building2 } from 'lucide-react';
import { Form, Button, Card, Container, Row, Col, Image, Alert } from 'react-bootstrap';
import { useAlert } from '../context/AlertContext';

interface CompanySettingsData {
    id?: number;
    legal_name: string;
    trade_name: string;
    address: string;
    email: string;
    phone: string;
    logo?: string | null;
    allow_new_registrations?: boolean;
}

const CompanySettings = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState<CompanySettingsData>({
        legal_name: '',
        trade_name: '',
        address: '',
        email: '',
        phone: ''
    });
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const { showAlert } = useAlert();

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/company/settings/');
            setFormData(response.data);
        } catch (error) {
            console.error('Error fetching company settings:', error);
            showAlert('Erro ao carregar configurações da empresa.', { type: 'error' });
        }
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
        setFormData(prev => ({ ...prev, logo: null }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const submitData = new FormData();
            submitData.append('legal_name', formData.legal_name);
            submitData.append('trade_name', formData.trade_name);
            submitData.append('address', formData.address || '');
            submitData.append('email', formData.email || '');
            submitData.append('phone', formData.phone || '');
            
            if (logoFile) {
                submitData.append('logo', logoFile);
            } else if (formData.logo === null && !logoPreview) {
                // If explicitly removed and no new file (API handling for clearing might vary, 
                // but usually empty string or null helps if backend supports it. 
                // Django REST often ignores missing fields in PATCH, so we might need specific logic 
                // if we want to CLEAR the logo. For now, assuming standard upload behavior).
                // If the backend doesn't support clearing via empty string, we might skip this.
                // But let's try sending empty string if supported or just not sending 'logo' key.
            }

            const response = await api.patch('/company/settings/', submitData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            setFormData(response.data);
            setLogoFile(null);
            setLogoPreview(null);
            showAlert('Configurações atualizadas com sucesso!', { type: 'success' });
            
            // Dispatch a custom event so the sidebar can update immediately
            window.dispatchEvent(new Event('companySettingsUpdated'));
            
        } catch (error) {
            console.error('Error updating settings:', error);
            showAlert('Erro ao salvar configurações.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const displayLogo = logoPreview || formData.logo;

    return (
        <Container className="py-2">
            <div className="d-flex align-items-center mb-4">
                <h2 className="mb-0">Configurações da Empresa</h2>
            </div>

            <Card className="shadow-sm border-0">
                <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={8}>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nome Fantasia</Form.Label>
                                            <Form.Control
                                                value={formData.trade_name}
                                                onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                                                required
                                                placeholder="Ex: Clínica Bem Estar"
                                            />
                                            <Form.Text className="text-muted">
                                                Nome exibido para seus clientes e no cabeçalho do sistema.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Razão Social</Form.Label>
                                            <Form.Control
                                                value={formData.legal_name}
                                                onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                                                required
                                                placeholder="Ex: Razão Social LTDA"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Endereço Completo</Form.Label>
                                            <Form.Control
                                                value={formData.address}
                                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                                placeholder="Rua, Número, Bairro, Cidade - UF"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>E-mail de Contato</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                placeholder="contato@empresa.com"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Telefone</Form.Label>
                                            <Form.Control
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                placeholder="(00) 00000-0000"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>

                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Logotipo da Empresa</Form.Label>
                                    <div className="text-center p-3 border rounded bg-light">
                                        {displayLogo ? (
                                            <div className="position-relative d-inline-block">
                                                <div 
                                                    onClick={() => fileInputRef.current?.click()}
                                                    style={{ cursor: 'pointer' }}
                                                    className="mb-2"
                                                >
                                                    <Image
                                                        src={displayLogo}
                                                        alt="Logo Preview"
                                                        fluid
                                                        style={{
                                                            maxHeight: '150px',
                                                            maxWidth: '100%',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                </div>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={removeLogo}
                                                >
                                                    Remover Logo
                                                </Button>
                                            </div>
                                        ) : (
                                            <div
                                                className="d-flex flex-column align-items-center justify-content-center py-4"
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{ cursor: 'pointer', color: '#6c757d' }}
                                            >
                                                <Building2 size={48} className="mb-2" />
                                                <span>Clique para enviar logo</span>
                                            </div>
                                        )}
                                        <Form.Control
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoChange}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                    <Form.Text className="text-muted mt-2 d-block text-center">
                                        Formato recomendado: PNG ou JPG (max 2MB)
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-4">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                                className="d-flex align-items-center gap-2 px-4"
                            >
                                <Save size={18} /> {loading ? 'Salvando...' : 'Salvar Alterações'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default CompanySettings;
